/**
 * Accessibility Tools — consolidated from SPA implementation.
 *
 * Provides:
 * - WCAG 2.1 AA/AAA + WCAG 2.2 contrast checking
 * - Alpha-blended contrast calculation
 * - Color blindness simulation (8 types)
 * - Confusable color detection (Delta-E)
 * - Full palette audit with auto-fix proposals
 * - Batch contrast checking
 *
 * All functions are standalone (no class). Uses colorjs.io for color math.
 */
import Color from 'colorjs.io';
// ── Constants ───────────────────────────────────────────────────────────────
const MIN_FOCUS_INDICATOR_RATIO = 3;
// ── Internal helpers ────────────────────────────────────────────────────────
function applyAlphaBlend(color, alpha, backdrop) {
    if (alpha >= 1)
        return color;
    const src = color.to('srgb');
    const dst = backdrop.to('srgb');
    const blended = src.coords.map((component, index) => {
        const dstComponent = dst.coords[index] ?? 0;
        return component * alpha + dstComponent * (1 - alpha);
    });
    return new Color('srgb', blended);
}
function meetsLargeText(sizePx, isBold) {
    const threshold = isBold ? 18 : 24;
    return sizePx >= threshold;
}
function generateContrastSuggestions(fg) {
    const fgLab = fg.to('lab');
    const lightenAmount = 20;
    const darkenAmount = 20;
    const lightened = new Color('lab', [
        Math.min(100, fgLab.coords[0] + lightenAmount),
        fgLab.coords[1],
        fgLab.coords[2],
    ]);
    const darkened = new Color('lab', [
        Math.max(0, fgLab.coords[0] - darkenAmount),
        fgLab.coords[1],
        fgLab.coords[2],
    ]);
    return {
        lighten: lightened.to('srgb').toString({ format: 'hex' }),
        darken: darkened.to('srgb').toString({ format: 'hex' }),
    };
}
function getTargetMetadata(target, largeText) {
    switch (target) {
        case 'aaa':
            return {
                threshold: largeText ? 4.5 : 7,
                label: `WCAG 2.1 AAA (${largeText ? 'Large Text' : 'Normal Text'})`,
            };
        case 'wcag22':
            return {
                threshold: largeText ? 3 : 4.5,
                label: `WCAG 2.2 (${largeText ? 'Large Text' : 'Small Text'})`,
            };
        case 'aa':
        default:
            return {
                threshold: largeText ? 3 : 4.5,
                label: `WCAG 2.1 AA (${largeText ? 'Large Text' : 'Normal Text'})`,
            };
    }
}
function evaluateTargetCompliance(ratio, target, largeText) {
    const { threshold, label } = getTargetMetadata(target, largeText);
    const isCompliant = typeof ratio === 'number' ? ratio >= threshold : false;
    const deficit = isCompliant || ratio === null ? 0 : Number((threshold - ratio).toFixed(2));
    const failingReasons = [];
    if (!isCompliant) {
        failingReasons.push(`Requires ≥ ${threshold.toFixed(2)}:1 (${label})`);
        if (ratio === null) {
            failingReasons.push('Contrast could not be calculated for this pair.');
        }
    }
    return { target, targetLabel: label, threshold, isCompliant, ratio, deficit, failingReasons };
}
function buildAutoFixProposals(foreground, background, contrastOptions, baseResult, target, largeText) {
    if (!baseResult)
        return [];
    const { threshold, label } = getTargetMetadata(target, largeText);
    const baseRatio = baseResult.ratio ?? 0;
    const proposals = [];
    const attempt = (role, suggestedColor, description) => {
        const original = (role === 'foreground' ? foreground : background).toLowerCase();
        const suggested = suggestedColor.toLowerCase();
        if (original === suggested)
            return;
        const nextFg = role === 'foreground' ? suggestedColor : foreground;
        const nextBg = role === 'background' ? suggestedColor : background;
        const recalculated = calculateContrast(nextFg, nextBg, contrastOptions);
        if (!recalculated)
            return;
        const compliance = evaluateTargetCompliance(recalculated.ratio, target, largeText);
        const ratioGain = Number((recalculated.ratio - baseRatio).toFixed(2));
        proposals.push({
            id: `${role}-${description.toLowerCase().replace(/\s+/g, '-')}-${suggested}`,
            role,
            description,
            originalColor: role === 'foreground' ? foreground : background,
            suggestedColor,
            resultingRatio: Number(recalculated.ratio.toFixed(2)),
            ratioGain,
            achievesTarget: compliance.isCompliant,
            targetLabel: label,
            threshold,
        });
    };
    try {
        const suggestions = generateContrastSuggestions(new Color(foreground));
        attempt('foreground', suggestions.lighten, 'Lighten foreground');
        attempt('foreground', suggestions.darken, 'Darken foreground');
    }
    catch { /* ignore */ }
    try {
        const suggestions = generateContrastSuggestions(new Color(background));
        attempt('background', suggestions.lighten, 'Lighten background');
        attempt('background', suggestions.darken, 'Darken background');
    }
    catch { /* ignore */ }
    return proposals.sort((a, b) => b.ratioGain - a.ratioGain);
}
// ── Public API ──────────────────────────────────────────────────────────────
/**
 * Calculate contrast ratio between two colors with WCAG compliance checks.
 * Supports alpha blending against a backdrop color.
 */
export function calculateContrast(foreground, background, options = {}) {
    try {
        const autoFg = new Color(foreground);
        const autoBg = new Color(background);
        const { backdrop = '#ffffff', textSizePx = 16, isBold = false } = options;
        const fgAlpha = options.foregroundAlpha !== undefined
            ? options.foregroundAlpha
            : (autoFg.alpha ? Number(autoFg.alpha) : 1);
        const bgAlpha = options.backgroundAlpha !== undefined
            ? options.backgroundAlpha
            : (autoBg.alpha ? Number(autoBg.alpha) : 1);
        const backdropColor = new Color(backdrop);
        const foregroundColor = applyAlphaBlend(new Color(foreground), fgAlpha, backdropColor);
        const backgroundColor = applyAlphaBlend(new Color(background), bgAlpha, backdropColor);
        const ratio = Math.abs(foregroundColor.contrast(backgroundColor, 'WCAG21'));
        const isLargeText = meetsLargeText(textSizePx, isBold);
        const result = {
            ratio,
            wcag: {
                aa: {
                    normalText: ratio >= 4.5,
                    largeText: ratio >= 3,
                    ui: ratio >= MIN_FOCUS_INDICATOR_RATIO,
                    focusIndicator: ratio >= MIN_FOCUS_INDICATOR_RATIO,
                },
                aaa: {
                    normalText: ratio >= 7,
                    largeText: ratio >= 4.5,
                },
                twoPointTwo: {
                    smallText: ratio >= 4.5,
                    largeText: ratio >= 3,
                    enhancedFocusIndicator: ratio >= 3,
                },
            },
            effective: {
                foreground: foregroundColor.to('srgb').toString({ format: 'hex' }),
                background: backgroundColor.to('srgb').toString({ format: 'hex' }),
                foregroundAlpha: Number(fgAlpha),
                backgroundAlpha: Number(bgAlpha),
            },
            textSizePx,
            isBold,
        };
        if ((isLargeText && result.wcag.aa.largeText) || (!isLargeText && result.wcag.aa.normalText)) {
            // already compliant; no suggestions
        }
        else if (ratio < 4.5) {
            result.suggestions = generateContrastSuggestions(foregroundColor);
        }
        return result;
    }
    catch {
        return null;
    }
}
/**
 * Check a single pair for AA/AAA compliance. Returns a simplified result.
 */
export function checkAACompliance(foreground, background, options = {}) {
    const result = calculateContrast(foreground, background, options);
    if (!result)
        return null;
    let level = 'Fail';
    if (result.wcag.aaa.normalText)
        level = 'AAA';
    else if (result.wcag.aa.normalText)
        level = 'AA';
    return { compliant: result.wcag.aa.normalText, level, ratio: result.ratio };
}
/**
 * Check contrast for all foreground×background combinations.
 */
export function batchContrastCheck(foregrounds, backgrounds, options = {}) {
    const results = [];
    for (const fg of foregrounds) {
        for (const bg of backgrounds) {
            results.push({ fg, bg, result: calculateContrast(fg, bg, options) });
        }
    }
    return results;
}
/**
 * Simulate color blindness by applying transformation matrices.
 */
export function simulateColorBlindness(color, type) {
    try {
        const c = new Color(color);
        const [r, g, b] = c.to('srgb').coords.map((v) => v * 255);
        let [rSim, gSim, bSim] = [r, g, b];
        switch (type) {
            case 'protanopia':
                rSim = 0.567 * r + 0.433 * g;
                gSim = 0.558 * r + 0.442 * g;
                bSim = 0.242 * g + 0.758 * b;
                break;
            case 'deuteranopia':
                rSim = 0.625 * r + 0.375 * g;
                gSim = 0.7 * r + 0.3 * g;
                bSim = 0.3 * g + 0.7 * b;
                break;
            case 'tritanopia':
                rSim = 0.95 * r + 0.05 * g;
                gSim = 0.433 * g + 0.567 * b;
                bSim = 0.475 * g + 0.525 * b;
                break;
            case 'protanomaly':
                rSim = 0.817 * r + 0.183 * g;
                gSim = 0.333 * r + 0.667 * g;
                bSim = 0.125 * g + 0.875 * b;
                break;
            case 'deuteranomaly':
                rSim = 0.8 * r + 0.2 * g;
                gSim = 0.258 * r + 0.742 * g;
                bSim = 0.142 * g + 0.858 * b;
                break;
            case 'tritanomaly':
                rSim = 0.967 * r + 0.033 * g;
                gSim = 0.733 * g + 0.267 * b;
                bSim = 0.183 * g + 0.817 * b;
                break;
            case 'achromatopsia': {
                const gray = 0.299 * r + 0.587 * g + 0.114 * b;
                rSim = gSim = bSim = gray;
                break;
            }
            case 'achromatomaly': {
                const grayValue = 0.299 * r + 0.587 * g + 0.114 * b;
                rSim = 0.618 * r + 0.382 * grayValue;
                gSim = 0.618 * g + 0.382 * grayValue;
                bSim = 0.618 * b + 0.382 * grayValue;
                break;
            }
        }
        const simulated = new Color('srgb', [rSim / 255, gSim / 255, bSim / 255]);
        return simulated.toString({ format: 'hex' });
    }
    catch {
        return null;
    }
}
/**
 * Detect pairs of colors that are too visually similar (confusable).
 */
export function detectConfusableColors(colors, threshold = 5) {
    const confusable = [];
    for (let i = 0; i < colors.length; i++) {
        for (let j = i + 1; j < colors.length; j++) {
            try {
                const c1 = new Color(colors[i]);
                const c2 = new Color(colors[j]);
                const deltaE = c1.deltaE2000(c2);
                if (deltaE < threshold) {
                    confusable.push([colors[i], colors[j], deltaE]);
                }
            }
            catch {
                continue;
            }
        }
    }
    return confusable;
}
/**
 * Full palette accessibility audit with compliance checking, auto-fix
 * proposals, and confusable pair detection.
 */
export function auditPalette(colors, options = {}) {
    const { target = 'aa', confusableDeltaE = 5, includeSelfComparisons = false, textSizePx = 16, isBold = false, ...contrastOverrides } = options;
    const contrastOptions = { ...contrastOverrides, textSizePx, isBold };
    const largeText = meetsLargeText(textSizePx, isBold);
    const passes = [];
    const failures = [];
    let totalPairs = 0;
    let passCount = 0;
    let failCount = 0;
    let worstRatio = null;
    let worstPairId;
    for (let i = 0; i < colors.length; i++) {
        for (let j = 0; j < colors.length; j++) {
            if (!includeSelfComparisons && i === j)
                continue;
            const fg = colors[i];
            const bg = colors[j];
            const pairId = `${i}-${j}`;
            const contrastResult = calculateContrast(fg, bg, contrastOptions);
            const ratio = contrastResult?.ratio ?? null;
            const compliance = evaluateTargetCompliance(ratio, target, largeText);
            const autoFixes = compliance.isCompliant
                ? []
                : buildAutoFixProposals(fg, bg, contrastOptions, contrastResult, target, largeText);
            const pairResult = {
                id: pairId,
                foreground: fg,
                background: bg,
                ratio: ratio !== null ? Number(ratio.toFixed(2)) : null,
                contrastResult,
                compliance,
                autoFixes,
            };
            totalPairs += 1;
            if (compliance.isCompliant) {
                passCount += 1;
                passes.push(pairResult);
            }
            else {
                failCount += 1;
                failures.push(pairResult);
            }
            if (typeof ratio === 'number' && (worstRatio === null || ratio < worstRatio)) {
                worstRatio = ratio;
                worstPairId = pairId;
            }
        }
    }
    const passRate = totalPairs === 0 ? 100 : Number(((passCount / totalPairs) * 100).toFixed(0));
    const confusablePairs = detectConfusableColors(colors, confusableDeltaE)
        .map(([first, second, deltaE]) => ({ first, second, deltaE: Number(deltaE.toFixed(2)) }))
        .sort((a, b) => a.deltaE - b.deltaE);
    const { label } = getTargetMetadata(target, largeText);
    return {
        timestamp: Date.now(),
        target,
        targetLabel: label,
        textSizePx,
        isBold,
        totalPairs,
        passCount,
        failCount,
        passRate,
        worstRatio: worstRatio !== null ? Number(worstRatio.toFixed(2)) : null,
        worstPairId,
        failures,
        passes,
        confusablePairs,
    };
}
//# sourceMappingURL=accessibilityTools.js.map